import React, { useState, useEffect, useRef } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Question Type Definitions
interface BaseQuestion {
    type: string;
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    correct: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[]; // Fill-in options for user to choose
    correct: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    correct: boolean;
}

interface MatchThePairQuestion extends BaseQuestion {
    type: "match";
    options: string[];
    labeled: { "A": string[], "B": string[] };
    correct: string;
}

// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion
    | MatchThePairQuestion;

// Define types
type SyllabusType =
    | "All Chapters"


interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
    syllabus?: SyllabusType;
}



const syllabusFiles: Record<SyllabusType, string> = {
    "All Chapters": "PeopleHelpUs.json"
}

const PeopleHelpUs: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        const topics = Object.keys(syllabusFiles) as SyllabusType[];
        const randomTopic = topics[Math.floor(Math.random() * topics.length)];
        const filename = syllabusFiles[randomTopic];

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "/" + filename
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/${filename}`
                );
                data = module.default;
            }
            setQuestions(shuffleArray(data));
            const choosedOne = data[Math.floor(Math.random() * data.length)];
            setCurrentQuestion(choosedOne);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.correct;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false" || currentQuestion.type === "match") {
            correctAnswer = currentQuestion.correct;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                setShowImage(false);
                loadRandomSyllabusQuestions();
                setSelectedOption(null);
            }, 4000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "People Who Help Us Worksheets for LKG | Community Helpers Activities";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach LKG kids about community helpers with interactive worksheets. Learn about people who help us like doctors, teachers, police, and more with fun activities and matching games." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>People Who Help Us</h1>

                            <p>
                                Our daily lives are made easier by many wonderful people — <strong>doctors, teachers, police officers, firefighters, postmen, and more</strong>. This worksheet pack introduces LKG children to <strong>community helpers</strong> through engaging visuals and simple activities, helping them recognize and appreciate those who serve society.
                            </p>

                            <h2>👩‍⚕️ What Are Community Helpers?</h2>
                            <p>
                                Community helpers are the people who help us in different ways. They provide essential services like health care, safety, education, and cleanliness. Teaching young children about these roles builds <strong>respect, awareness, and gratitude</strong>.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify common helpers: <strong>doctor, teacher, police, firefighter, postman, etc</strong>.</li>
                                <li>Recognize the tools and uniforms used by different helpers</li>
                                <li>Understand how each helper supports our community</li>
                                <li>Match the helper to their work or place</li>
                            </ul>

                            <h2>📋 Activities Included in the Worksheets</h2>
                            <ul>
                                <li>Match the helper to the job (<strong>e.g. Doctor → Hospital</strong>)</li>
                                <li>Circle the correct helper based on a picture</li>
                                <li>Identify tools used by different helpers</li>
                                <li>Color the uniforms or vehicles of helpers</li>
                                <li>Simple Q&A about who helps us when we are sick, in danger, or need to learn</li>
                            </ul>

                            <h2>👨‍👩‍👧 Tips for Parents and Teachers</h2>
                            <ul>
                                <li>Use flashcards or picture books showing helpers in action</li>
                                <li>Talk about your real-life experience with <strong>doctors, police, or teachers</strong></li>
                                <li>Role-play using costumes or toys (<strong>e.g. doctor kit</strong>)</li>
                                <li>Take your child to meet a postman or visit a fire station (if possible)</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/general-awareness/body-parts"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Body Parts</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/general-awareness/seasons"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Seasons</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/general-awareness/transports"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Transports</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Turn everyday experiences into learning moments — when visiting a doctor or seeing a garbage truck, explain how these people help us.
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>People Who Help Us</h1>

                        <p>
                            Our daily lives are made easier by many wonderful people — <strong>doctors, teachers, police officers, firefighters, postmen, and more</strong>. This worksheet pack introduces LKG children to <strong>community helpers</strong> through engaging visuals and simple activities, helping them recognize and appreciate those who serve society.
                        </p>

                        <h2>👩‍⚕️ What Are Community Helpers?</h2>
                        <p>
                            Community helpers are the people who help us in different ways. They provide essential services like health care, safety, education, and cleanliness. Teaching young children about these roles builds <strong>respect, awareness, and gratitude</strong>.
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Identify common helpers: <strong>doctor, teacher, police, firefighter, postman, etc</strong>.</li>
                            <li>Recognize the tools and uniforms used by different helpers</li>
                            <li>Understand how each helper supports our community</li>
                            <li>Match the helper to their work or place</li>
                        </ul>

                        <h2>📋 Activities Included in the Worksheets</h2>
                        <ul>
                            <li>Match the helper to the job (<strong>e.g. Doctor → Hospital</strong>)</li>
                            <li>Circle the correct helper based on a picture</li>
                            <li>Identify tools used by different helpers</li>
                            <li>Color the uniforms or vehicles of helpers</li>
                            <li>Simple Q&A about who helps us when we are sick, in danger, or need to learn</li>
                        </ul>

                        <h2>👨‍👩‍👧 Tips for Parents and Teachers</h2>
                        <ul>
                            <li>Use flashcards or picture books showing helpers in action</li>
                            <li>Talk about your real-life experience with <strong>doctors, police, or teachers</strong></li>
                            <li>Role-play using costumes or toys (<strong>e.g. doctor kit</strong>)</li>
                            <li>Take your child to meet a postman or visit a fire station (if possible)</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/lkg/general-awareness/body-parts"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Body Parts</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/general-awareness/seasons"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Seasons</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/general-awareness/transports"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Transports</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            Turn everyday experiences into learning moments — when visiting a doctor or seeing a garbage truck, explain how these people help us.
                        </p>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.correct).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                            {currentQuestion.type === "match" && (
                                <>
                                    <table className="table-bordered table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Column A</th>
                                                <th scope="col">Column B</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentQuestion.labeled.A.map((leftItem, index) => (
                                                <tr key={index}>
                                                    <td>{leftItem}</td>
                                                    <td>{currentQuestion.labeled.B[index]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                </>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default PeopleHelpUs;
